'use client';

import React, { useEffect, useState, ReactNode, createContext, useContext } from 'react';

// Context to track GSAP ready state
const GSAPReadyContext = createContext<boolean>(false);

export function useGSAPReady() {
  return useContext(GSAPReadyContext);
}

interface GSAPProviderProps {
  children: ReactNode;
}

export function GSAPProvider({ children }: GSAPProviderProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cleanup: (() => void) | undefined;

    // PERFORMANCE: Dynamically import GSAP after initial paint
    // This reduces render-blocking JavaScript by ~50KB
    const loadGSAP = async () => {
      // Use requestIdleCallback to load after main content is painted
      const load = async () => {
        const [{ gsap }, { ScrollTrigger }] = await Promise.all([
          import('gsap'),
          import('gsap/ScrollTrigger'),
        ]);

        // Register plugin
        gsap.registerPlugin(ScrollTrigger);

    // Configure GSAP globally
    gsap.config({
          nullTargetWarn: false,
          autoSleep: 60,
          force3D: 'auto',
    });

    // Configure ScrollTrigger globally
    ScrollTrigger.config({
          limitCallbacks: true,
          syncInterval: 200, // Increased from 150ms for better performance
          ignoreMobileResize: true,
          autoRefreshEvents: 'visibilitychange,load', // Removed DOMContentLoaded - already fired
    });

        // Set up responsive refresh
    const matchMedia = gsap.matchMedia();
    
    matchMedia.add('(min-width: 768px)', () => {
      ScrollTrigger.refresh();
    });

    matchMedia.add('(max-width: 767px)', () => {
      ScrollTrigger.refresh();
    });

        // Initial refresh - delayed to ensure content is rendered
    const initialRefreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
          setIsReady(true);
        }, 200);

        // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
          }, 200);
    };

        window.addEventListener('resize', handleResize, { passive: true });

        // Visibility change handler
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ScrollTrigger.refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

        // Store cleanup function
        cleanup = () => {
      clearTimeout(initialRefreshTimeout);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      ScrollTrigger.getAll().forEach((trigger) => {
        trigger.kill();
      });
      
      matchMedia.revert();
        };
      };

      // Use requestIdleCallback if available, otherwise use setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => load(), { timeout: 2000 });
      } else {
        // Fallback: load after a short delay to not block initial render
        setTimeout(() => load(), 100);
      }
    };

    loadGSAP();

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <GSAPReadyContext.Provider value={isReady}>
      {children}
    </GSAPReadyContext.Provider>
  );
}

















